import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../../../environments/environment';

declare global {
  interface Window {
    ChimeSDK: any;
  }
}

export type CallState = 'idle' | 'connecting' | 'connected' | 'error';

interface StartCallResponse {
  success: boolean;
  contactId: string;
  participantId: string;
  participantToken: string;
  connectionData: {
    Meeting: any;
    Attendee: any;
  };
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class CallService {
  private meetingSession: any = null;
  private apiUrl = environment.urls.apiUrl || '';

  callState$ = new BehaviorSubject<CallState>('idle');
  isMuted$ = new BehaviorSubject<boolean>(false);
  error$ = new BehaviorSubject<string>('');
  logs$ = new BehaviorSubject<string[]>([]);

  constructor(private zone: NgZone) {}

  private log(msg: string) {
    this.zone.run(() => {
      const time = new Date().toLocaleTimeString();
      const entry = `[${time}] ${msg}`;
      this.logs$.next([...this.logs$.value, entry]);
      console.log(entry);
    });
  }

  // Public API: default call
  async startCall(audioElement: HTMLAudioElement): Promise<void> {
    return this.startCallInternal(audioElement);
  }

  // Public API: emergency call with specific contact flow
  async startEmergencyCall(audioElement: HTMLAudioElement, contactFlowId: string): Promise<void> {
    return this.startCallInternal(audioElement, contactFlowId);
  }

  // Internal implementation shared by both
  private async startCallInternal(audioElement: HTMLAudioElement, contactFlowId?: string): Promise<void> {
    this.callState$.next('connecting');
    this.error$.next('');
    this.log('Starting call...');

    if (!window.ChimeSDK) {
      this.log('ERROR: ChimeSDK not loaded');
      this.error$.next('ChimeSDK not loaded. Refresh the page.');
      this.callState$.next('error');
      return;
    }
    this.log('ChimeSDK loaded OK');

    try {
      this.log('Requesting mic...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      this.log('Mic access granted');
    } catch (err: any) {
      this.log('Mic FAILED: ' + err.message);
      this.error$.next('Microphone access denied. Allow it and retry.');
      this.callState$.next('error');
      return;
    }

    let data: StartCallResponse;
    try {
      this.log('Calling .NET API at /api/WebRtcCall/start-call');

      const body: any = { DisplayName: 'Website Visitor' };
      if (contactFlowId) {
        body.ContactFlowId = contactFlowId;
      }

      const response = await fetch(`${this.apiUrl}/api/WebRtcCall/start-call`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      this.log('Response status ' + response.status);
      data = await response.json();
      this.log('success=' + data.success);

      if (!data.success) {
        throw new Error(data.error || 'API returned failure');
      }
      this.log('ContactId=' + data.contactId);
    } catch (err: any) {
      this.log('API FAILED: ' + err.message);
      this.error$.next(err.message);
      this.callState$.next('error');
      return;
    }

    try {
      this.log('Starting Chime audio session...');
      await this.initChimeSession(data!.connectionData, audioElement);
      this.log('Chime session started');
    } catch (err: any) {
      this.log('Chime FAILED: ' + err.message);
      this.error$.next(err.message);
      this.callState$.next('error');
      this.endCall();
    }
  }

  private async initChimeSession(
    connectionData: { Meeting: any; Attendee: any },
    audioElement: HTMLAudioElement
  ): Promise<void> {
    const SDK = window.ChimeSDK;

    const logger = new SDK.ConsoleLogger('ChimeSDK', SDK.LogLevel.INFO);
    const configuration = new SDK.MeetingSessionConfiguration(
      connectionData.Meeting,
      connectionData.Attendee
    );

    const deviceController = new SDK.DefaultDeviceController(logger);
    this.meetingSession = new SDK.DefaultMeetingSession(
      configuration,
      logger,
      deviceController
    );

    this.meetingSession.audioVideo.bindAudioElement(audioElement);

    const audioInputDevices =
      await this.meetingSession.audioVideo.listAudioInputDevices();
    if (audioInputDevices.length > 0) {
      await this.meetingSession.audioVideo.startAudioInput(
        audioInputDevices[0].deviceId
      );
      this.log('Mic selected: ' + audioInputDevices[0].label);
    }

    const audioOutputDevices =
      await this.meetingSession.audioVideo.listAudioOutputDevices();
    if (audioOutputDevices.length > 0) {
      await this.meetingSession.audioVideo.chooseAudioOutput(
        audioOutputDevices[0].deviceId
      );
    }

    const self = this;
    const observer = {
      audioVideoDidStart: () => {
        self.log('CONNECTED — audio session is live!');
        self.callState$.next('connected');
      },
      audioVideoDidStop: (status: any) => {
        self.log('Audio stopped (code: ' + status.statusCode() + ')');
        self.callState$.next('idle');
      },
      audioVideoDidStartConnecting: (reconnecting: boolean) => {
        self.log(reconnecting ? 'Reconnecting...' : 'Connecting audio...');
      },
    };

    this.meetingSession.audioVideo.addObserver(observer);
    this.meetingSession.audioVideo.start();
  }

  toggleMute(): void {
    if (!this.meetingSession) return;
    if (this.isMuted$.value) {
      this.meetingSession.audioVideo.realtimeUnmuteLocalAudio();
      this.isMuted$.next(false);
      this.log('Unmuted');
    } else {
      this.meetingSession.audioVideo.realtimeMuteLocalAudio();
      this.isMuted$.next(true);
      this.log('Muted');
    }
  }

  endCall(): void {
    if (this.meetingSession) {
      this.meetingSession.audioVideo.stop();
      this.meetingSession = null;
    }
    this.isMuted$.next(false);
    this.callState$.next('idle');
    this.log('Call ended');
  }
}
